// ---------------------------------------------------------------------------
// The one prop every frame on this screen takes, and the sequence of slides it
// is drawn into.
//
// `RevealView` is the seam the split is built on: a frame is handed this and
// nothing else, so no piece of the reveal reaches into `ClientGameState` for
// itself and no piece of it holds state of its own. `buildSlides` is the other
// half — what the round HAS, in order — and `payoffOrder` is the closing run
// that the beat lets out one at a time.
//
// The two live together because the shape of the view is what decides what
// slides there can be. A `.ts` and not a `.tsx` on purpose: this file works out
// what is on the stage and draws none of it.
// ---------------------------------------------------------------------------

import type {
  BestAnswer,
  ClientGameState,
  ClientRound,
  ClientSubmission,
  PublicPlayer,
} from '@countdown/shared';
import type { LetterTileSize } from '../../../components/primitives/LetterTile.js';
import { playerById } from '../../../lib/playerLookup.js';
import { aggregateOf, releasedAggregateRows } from './aggregate.js';

// --- Shared view -----------------------------------------------------------

export interface RevealView {
  state: ClientGameState;
  round: ClientRound;
  /** Every seated player, in the server's reveal order. */
  ordered: PublicPlayer[];
  /** Whose answer the server has let out, in the order it let them out. */
  released: string[];
  isReleased: (playerId: string) => boolean;
  submissionOf: (playerId: string) => ClientSubmission | undefined;
  declaredOf: (playerId: string) => number | null;
  /** Every entry is out. The payoff and the control bar wait on this, not on a timer. */
  finished: boolean;
  best: BestAnswer | null;
  /** How many of the closing slides the beat has let onto the stage. */
  beats: number;
  /** Big tiles once there is room for them, which is a desktop and nothing else. */
  tileSize: LetterTileSize;
  onOverride: (playerId: string, allow: boolean) => void;
  /** Take a ruling back off a word. See `undoRulingMessage`. */
  onUndoRuling: (playerId: string) => void;

  // --- What a word means -----------------------------------------------------
  //
  // Three fields rather than a callback per frame, for the same reason
  // everything else here is on the view: a frame takes this and nothing else,
  // so making a word askable is one prop away wherever a word is drawn.

  /**
   * The id to ask the live room what a word on this stage means, or null when
   * nothing on it may be asked about at all.
   *
   * **`liveGameId` and never `gameId`.** `ClientGameState.gameId` is the id of
   * the written RECORD and is null for the whole of a live game, which is why
   * the reveal could not be wired when definitions shipped everywhere else;
   * `liveGameId` is the room's own game id and is set from the moment the game
   * starts. See the doc comment on both fields in `@countdown/shared`.
   *
   * Null is the mid-round gate, and it is a gate the client keeps rather than
   * discovers: this screen is post-judging by definition, but a round with no
   * `revealedAt` has revealed nothing, the endpoint refuses every word of it,
   * and a control whose only outcome is a refusal is not a control. With this
   * null, `AskableWord` draws the word and no button at all.
   */
  definitionGameId: string | null;
  /**
   * The word the stage is being asked about, uppercase, or null. One per
   * screen: one answer is on the stage at a time, so a second line would be a
   * line about something nobody can see.
   */
  pickedWord: string | null;
  /**
   * `anchor` is the button the word is drawn as, and it is what the answer is
   * placed against: the gloss floats beside its word now rather than landing in
   * a slot under the stage. See `WordDefinition.tsx` for why that changed.
   */
  onPickWord: (word: string, anchor: HTMLElement) => void;
}

/**
 * What the stage is showing. The round's own body first, then the payoff.
 *
 * `leaderboard` is the whole letters reveal in one slide: it does not change as
 * answers land, it grows, so the cursor sits still while the server releases
 * and the panel does the moving.
 */
export type Slide =
  | { kind: 'answer'; key: string; player: PublicPlayer }
  | { kind: 'leaderboard'; key: 'leaderboard' }
  | { kind: 'aggregate'; key: 'aggregate' }
  | { kind: 'distribution'; key: 'distribution' }
  | { kind: 'best'; key: 'best' }
  | { kind: 'shadows'; key: 'shadows' }
  | { kind: 'scores'; key: 'scores' };

const AGGREGATE_SLIDE: Slide = { kind: 'aggregate', key: 'aggregate' };

/**
 * The closing slides, in the order the round wants them, whether or not the
 * beat has let them out yet.
 *
 * There is no everything slide any more, and nothing replaced it: the letters
 * leaderboard *is* the round seen whole, and it has been on the stage the
 * entire time rather than arriving as a recap of a screen you were already
 * looking at.
 *
 * **A numbers round closes the same way a letters round does, and it used to
 * close backwards.** The scores came first and the solver's line after them, on
 * the reasoning that "here is what was there" is not a verdict on anybody. What
 * that produced was a standing that was not the last thing on the screen and a
 * solution nobody was looking for by the time it arrived. The order is the
 * round's: the answers, then the best there was, then what it did to the game.
 *
 * A conundrum has no payoff but the scores — the one thing worth seeing is
 * already the slide before them.
 */
export function payoffOrder(view: RevealView): Slide[] {
  const scores: Slide = { kind: 'scores', key: 'scores' };
  const best: Slide[] = view.best === null ? [] : [{ kind: 'best', key: 'best' }];

  // **The round seen whole, and only when the round was drawn as a
  // distribution.** It is first in the payoff rather than last, because it is
  // the table's own footnote — the four hundred seats the eight rows did not
  // name — and putting it after the best answer would step on the ending. An
  // ordinary four-player round has no aggregate and gains no slide.
  const distribution: Slide[] =
    aggregateOf(view.round) === null ? [] : [{ kind: 'distribution', key: 'distribution' }];

  if (view.round.type === 'conundrum') {
    // **What the AI opponents had, before the standing.** A conundrum takes one
    // buzz, so in a room of four models three of them finished an answer that
    // the round threw away — Isaac's words, that it feels like such a waste to
    // have them all compute an answer and not use it. It gets a slide of its
    // own rather than a line under the solution, because it is the only screen
    // in the game where you can watch several models be asked the same question
    // and compare what they said.
    //
    // Only when there is something to show, so an all-human room and a room
    // whose models all came back empty are unchanged.
    const shadows: Slide[] =
      (view.round.shadowAnswers ?? []).length > 0 ? [{ kind: 'shadows', key: 'shadows' }] : [];
    return [...distribution, ...shadows, scores];
  }
  return [...distribution, ...best, scores];
}

/**
 * The slides a round has, before the beat decides how many of the closing ones
 * are out.
 *
 * **The conundrum is one slide and then the scores, and collapsing it fixed a
 * bug rather than only a shape.** Conundrum submissions exist only for players
 * who buzzed, so a round with a single buzzer has a one-entry queue: the
 * solver's entry is released and the round completes on the same tick, and a
 * complete reveal releases every seated player at once. The cursor followed the
 * server to the last of them — somebody who never buzzed, drawn as a dash — and
 * the solver's frame was created and stepped over in the same frame. The one
 * thing anybody wanted to see was the one thing nobody could. With no buzzes at
 * all it was worse: every player got a slide in the same instant.
 *
 * So a conundrum shows what happened and nothing else: the solver's answer, or
 * the word it was built from when nobody got it. Who buzzed and failed is a
 * fact about the round, not an answer to reveal, and the strip above the stage
 * already says it in one line.
 *
 * **And it shows it at once, rather than waiting for the cursor.** The cursor
 * exists to pace a queue, and a conundrum's queue is one answer — the one the
 * whole room is already looking at nine letters of. Holding it back bought a
 * frame that said the reveal was coming, which is the reveal telling the room to
 * wait for the reveal.
 */
export function buildSlides(view: RevealView): Slide[] {
  const slides: Slide[] = [];
  const aggregate = aggregateOf(view.round);

  // --- The aggregated round --------------------------------------------------
  //
  // **One slide replaces the whole queue, on every round type.** The table is
  // the body of the reveal and it fills itself worst-first as the server's
  // cursor moves; `aggregate.ts` holds the whole of that argument, including why
  // the best row is withheld until the trailing step and why nothing here calls
  // `revealHoldMs`.
  //
  // An ordinary game never reaches this branch. `aggregate` is null on every
  // round of one, so four players get exactly the reveal they had.
  if (aggregate !== null) {
    if (view.round.type === 'conundrum') {
      // **No steps, in either mode.** CLAUDE.md, and `revealStepCount` returns
      // 0 for a conundrum: the whole of its reveal is available the instant it
      // is judged, so the table is out at once rather than being paced against a
      // cursor that will never move.
      slides.push(AGGREGATE_SLIDE);
      // The word it was built from. There is no solver frame on an aggregated
      // conundrum in either mode — with the buzzer off there is no single
      // solver to draw, and with it on the solver is a row on the table like
      // everybody else — so the solution is a slide of its own here, exactly as
      // it is for an unsolved conundrum in an ordinary room.
      if (view.finished && view.best !== null) slides.push({ kind: 'best', key: 'best' });
    } else if (releasedAggregateRows(view.round).length > 0) {
      slides.push(AGGREGATE_SLIDE);
    }
    if (!view.finished) return slides;
    return [...slides, ...payoffOrder(view).slice(0, view.beats)];
  }

  if (view.round.type === 'letters') {
    if (view.released.length > 0) slides.push({ kind: 'leaderboard', key: 'leaderboard' });
  } else if (view.round.type === 'conundrum') {
    const solverId = view.round.solvedBy ?? null;
    const solver = solverId === null ? null : playerById(view.state, solverId);

    if (solver !== null) {
      slides.push({ kind: 'answer', key: `answer:${solver.id}`, player: solver });
    } else if (view.finished && view.best !== null) {
      slides.push({ kind: 'best', key: 'best' });
    }
  } else {
    const byId = new Map(view.ordered.map((player) => [player.id, player]));
    for (const playerId of view.released) {
      const player = byId.get(playerId);
      if (player === undefined) continue;
      slides.push({ kind: 'answer', key: `answer:${playerId}`, player });
    }
  }

  if (!view.finished) return slides;
  return [...slides, ...payoffOrder(view).slice(0, view.beats)];
}
